<template>
  <div class="mx-2 d-flex justify-space-between flex-column h-100">
    <div class="tasks-scrollable" v-show="hasTasks && !isInitialLoadingGroup">
      <div class="d-flex justify-space-between align-center w-100">
        <Button
          plain
          class="my-2"
          size="small"
          :text="
            showCompleted
              ? $t('tasks.hide_completed')
              : $t('tasks.show_completed')
          "
          icon="mdi-check-circle-outline"
          @click="handleShowCompleted"
        />
        <v-progress-circular
          v-if="$refs?.tasksGroup?.isLoading"
          indeterminate
          color="blue"
          size="18"
          class="mr-2"
        />
      </div>
      <v-divider />
      <TaskGroup
        hideOnEmpty
        viewMode="list"
        ref="tasksGroup"
        groupKey="all"
        :autorefreshOnQueryChange="false"
        :title="$t('tasks.all')"
        :groupFilters="groupFilters"
        :groupSort="{ by: 'status' }"
        :initialLimit="15"
        @selectTask="openTaskDrawer"
      />
    </div>
    <div
      v-if="(!hasTasks && isLoading) || isInitialLoadingGroup"
      class="tasks-scrollable"
    >
      <div class="mt-4 mx-1">
        <div class="d-flex justify-space-between">
          <v-skeleton-loader type="text" width="120" />
          <v-skeleton-loader type="text" width="25" />
        </div>
        <v-divider />
        <div v-for="index in 4" :key="index">
          <div class="d-flex justify-space-between align-center px-2 py-2">
            <div class="d-flex align-center">
              <v-skeleton-loader
                type="image"
                width="16"
                height="16"
                class="skeleton-circle"
              />
              <div class="ml-2">
                <v-skeleton-loader type="text" width="150" />
                <v-skeleton-loader type="text" width="90" />
              </div>
            </div>
            <div class="d-flex align-center mr-8">
              <v-skeleton-loader
                type="image"
                width="20"
                height="20"
                class="mr-2 skeleton-circle"
              />
              <v-skeleton-loader
                type="image"
                width="28"
                height="28"
                class="skeleton-circle"
              />
            </div>
          </div>
          <v-divider />
        </div>
      </div>
    </div>

    <div
      v-if="!hasTasks && !isLoading && !isInitialLoadingGroup"
      class="d-flex align-center justify-center flex-column h-100 mx-6"
    >
      <v-icon color="blue-grey lighten-1" size="32"
        >mdi-check-circle-outline</v-icon
      >
      <p class="subtitle-2 blue-grey--text text--darken-1 mt-2">
        {{
          isAiTasksSuggestionEnabled
            ? $t('tasks.create_first_task_now')
            : $t('tasks.no_tasks_created_yet')
        }}
      </p>
      <p class="caption blue-grey--text text-center">
        {{
          isAiTasksSuggestionEnabled
            ? $t('tasks.keep_up_great_work')
            : $t('tasks.create_first_task')
        }}
      </p>
      <Button
        plain
        :text="$t('tasks.create_task')"
        @click="openCreatorDrawer"
      />

      <div
        v-if="isAiTasksSuggestionEnabled && hasSolution"
        class="d-flex justify-center align-center mt-4"
      >
        <Button
          plain
          icon="mdi-creation-outline"
          :text="$t('tasks.suggest_tasks_ai')"
          @click="handleSuggestTasksWithAi"
        />
      </div>
    </div>
    <InlineCreator
      v-if="canEdit && hasTasks && !isLoading && !isInitialLoadingGroup"
      class="mt-6 mb-2"
      :entityId="modelId"
      :entityType="entityType"
      @addDetails="openCreatorDrawer"
      @taskWasCreated="handleTaskWasCreated"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import access from '@/lib/access'
import InlineCreator from '@/containers/TasksView/Header/InlineCreator.vue'
import TaskGroup from '@/containers/TasksView/Header/TaskGroup.vue'
import Button from '@/components/UI/Button.vue'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

export default {
  name: 'TasksTab',

  components: {
    InlineCreator,
    TaskGroup,
    Button,
  },

  mixins: [mixin, trackerMixin],

  props: {
    canEdit: {
      type: Boolean,
      required: true,
      default: false,
    },
    modelId: {
      type: String,
      required: true,
    },
    rev: {
      type: Object,
      required: false,
    },
    siteId: { type: String, required: true },
    organizationId: { type: String, required: true },
  },

  data() {
    return {
      hasTasks: false,
      isLoading: false,
      showCompleted: false,
      taskCreatedEvents: Array.from(
        new Set([
          HUB_EVENTS.TASK_CREATION_DRAWER.CREATED,
          HUB_EVENTS.AI.TASK_CREATED,
        ])
      ),
    }
  },

  created() {
    this.validateTasksCount()
  },

  watch: {
    '$route.query'() {
      if (!this.$route.query?.task) return

      eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE)
    },
  },

  mounted() {
    this.taskCreatedEvents.forEach(eventName => {
      eventHub.$on(eventName, this.handleTaskWasCreated)
    })

    eventHub.$on(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED, this.refreshGroups)

    if (this.$route.query?.task) {
      eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE)
    }
  },

  beforeDestroy() {
    this.taskCreatedEvents.forEach(eventName => {
      eventHub.$off(eventName, this.handleTaskWasCreated)
    })

    eventHub.$off(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED, this.refreshGroups)
  },

  methods: {
    async validateTasksCount() {
      this.isLoading = true
      const { data } = await api.tasks.getAll({
        organizationId: this.organizationId,
        filters: { entityId: this.modelId },
        limit: 1,
        projections: ['name'],
      })
      this.hasTasks = Boolean(data.totalRecordsInData ?? 0)
      this.isLoading = false
    },

    handleTaskWasCreated() {
      if (!this.hasTasks) {
        this.hasTasks = true
        this.resetGroupsLoading()
      }
      this.refreshGroups()
      this.showSuccess(this.$t('tasks.created_successfully'))
    },

    refreshGroups() {
      this.$refs.tasksGroup?.refreshList()
    },

    resetGroupsLoading() {
      this.$refs.tasksGroup?.resetInitialLoading()
    },

    openCreatorDrawer(task = {}) {
      eventHub.$emit(
        HUB_EVENTS.TASK_CREATION_DRAWER.CREATE,
        {
          ...task,
          entityId: this.modelId,
          entityType: this.entityType,
          entityName: this.rev.name,
          assignees: [this.account],
          isPrivate: false,
        },
        this.$route?.name
      )
    },

    openTaskDrawer(taskId) {
      eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE, taskId)
    },

    handleShowCompleted() {
      this.showCompleted = !this.showCompleted
      this.trackTasks('tasks.showCompletedTasks', {
        show: this.showCompleted,
        entity: this.$route?.name,
      })
      this.refreshGroups()
    },

    handleSuggestTasksWithAi() {
      eventHub.$emit(HUB_EVENTS.AI.HUB_DIALOG_OPEN, {
        rev: this.rev,
        suggestTasks: true,
      })
    },
  },

  computed: {
    ...mapGetters(['account', 'organization']),

    aiFeaturesAreEnabled() {
      return access.aiFeaturesAreEnabled(this.account, this.organization)
    },

    isAiTasksSuggestionEnabled() {
      return (
        this.aiFeaturesAreEnabled &&
        this.$isFeatureEnabled(this.$featuresKeys.aiTasksSuggestion)
      )
    },

    hasSolution() {
      return (
        this.rev?.afterPhase?.solution ??
        this.rev?.solutionDescription ??
        this.rev?.expand?.solution
      )
    },

    entityType() {
      if (this.rev.type === 'rev') return this.rev.data.revType
      return this.rev.type
    },

    isInitialLoadingGroup() {
      return this.$refs.tasksGroup?.isInitialLoading
    },

    groupFilters() {
      return {
        entityId: this.modelId,
        status: this.showCompleted
          ? []
          : [
              this.$constants.tasksStatus.open,
              this.$constants.tasksStatus.inProgress,
            ],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tasks-scrollable {
  max-height: calc(100% - 50px);
  overflow: auto;
}
</style>
